import env from '../../config/environment';
import React, { useEffect } from 'react';
import { RootState, useAppSelector } from '../../redux/store';
import {
  HeapProjectId,
  PromotionLevel,
  SingletonHooksContainer,
  useHeaper,
} from '@equipmentshare/heaper';

interface HeaperComponentProps {
  accessToken: string;
  idToken: string;
}

const HEAP_LOADER_MARKER = 'data-heap-loader';

const buildHeapCdnUrl = (projectId: HeapProjectId): string =>
  `https://cdn.us.heap-api.com/config/${projectId}/heap_config.js`;

const getHeapProjectId = (level: PromotionLevel): HeapProjectId | undefined =>
  HeapProjectId[level as keyof typeof HeapProjectId];

// Only remove the loader + CDN script for THIS project id, so we never touch
// scripts owned by another env, another integration, or a different projectId.
const removeHeapScriptsForProject = (projectId: HeapProjectId): void => {
  const selector = [
    `script[${HEAP_LOADER_MARKER}="${projectId}"]`,
    `script[src="${buildHeapCdnUrl(projectId)}"]`,
  ].join(', ');
  document.querySelectorAll(selector).forEach((el) => el.remove());
};

const HeaperProvider = () => {
  const authContext = useAppSelector((state: RootState) => state.auth.authContext);
  const accessToken = authContext?.accessToken ?? null;
  const idToken = authContext?.idToken ?? null;

  const projectId = getHeapProjectId(env.HEAPER_ENV as PromotionLevel);

  useEffect(() => {
    if (!projectId) return;

    if (!accessToken) {
      // On logout, drop both the loader snippet and the CDN script so the next
      // login starts from a clean window.heap (no stale identify or queue).
      removeHeapScriptsForProject(projectId);
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute(HEAP_LOADER_MARKER, String(projectId));
    script.innerHTML = `
      window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){console.log("[Heap] heap.load projectId:",e);window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","identifyHashed","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};

      heap.load(String(${JSON.stringify(projectId)}), { supportedDomains: ["estrack.com", "equipmentshare.com"] });
    `;
    document.head.appendChild(script);

    return () => {
      removeHeapScriptsForProject(projectId);
    };
  }, [accessToken, projectId]);

  return (
    <>
      <SingletonHooksContainer />
      {accessToken && idToken ? <HeaperSetup accessToken={accessToken} idToken={idToken} /> : null}
    </>
  );
};

const HeaperSetup: React.FC<HeaperComponentProps> = ({ accessToken, idToken }) => {
  const heaperEnv = env.HEAPER_ENV as PromotionLevel;

  const heaperOptions = {
    accessToken,
    idToken,
    heap: false,
    intercom: true, // only intercom
    silent: false,
  };

  useHeaper('EquipmentShare.com', heaperEnv, heaperOptions);

  return null;
};

export default HeaperProvider;
